import { calculateHeaderGroupHeight } from '@theme/critical';
import { Component } from '@theme/component';
import { onDocumentLoaded, changeMetaThemeColor } from '@theme/utilities';

class HeaderComponent extends Component {
  requiredRefs = ['headerDrawerContainer', 'headerMenu', 'headerRowTop'];

  #menuDrawerHiddenWidth = null;
  #intersectionObserver = null;
  #offscreen = false;
  #lastScrollTop = 0;
  #timeout = null;
  #animationDelay = 150;
  #scrollScheduled = false;
  #headerOffsetTop = 0;
  #lastHeaderHeight = null;

  #resizeObserver = new ResizeObserver(([entry]) => {
    if (!entry) return;

    const height = entry.contentRect.height;

    requestAnimationFrame(() => {
      if (this.#lastHeaderHeight !== height) {
        this.#lastHeaderHeight = height;
        document.body.style.setProperty('--header-height', `${height}px`);
      }
    });

    if (this.#menuDrawerHiddenWidth && window.innerWidth > this.#menuDrawerHiddenWidth) {
      this.#updateMenuVisibility(false);
    }
  });

  #observeStickyPosition = (alwaysSticky = true) => {
    if (this.#intersectionObserver) return;

    this.#intersectionObserver = new IntersectionObserver(([entry]) => {
      if (!entry) return;

      const isIntersecting = entry.isIntersecting;

      if (alwaysSticky) {
        this.dataset.stickyState = isIntersecting ? 'inactive' : 'active';
        changeMetaThemeColor(this.refs.headerRowTop);
      } else {
        this.#offscreen = !isIntersecting || this.dataset.stickyState === 'active';
      }
    }, { threshold: alwaysSticky ? 1 : 0 });

    this.#intersectionObserver.observe(this);
  };

  #handleOverflowMinimum = (event) => {
    this.#updateMenuVisibility(event.detail.minimumReached);
  };

  #updateMenuVisibility(hideMenu) {
    const { headerDrawerContainer, headerMenu } = this.refs;

    if (hideMenu) {
      headerDrawerContainer.classList.remove('desktop:hidden');
      headerMenu.classList.add('hidden');
      this.#menuDrawerHiddenWidth = window.innerWidth;
    } else {
      headerDrawerContainer.classList.add('desktop:hidden');
      headerMenu.classList.remove('hidden');
      this.#menuDrawerHiddenWidth = null;
    }
  }

  #handleWindowScroll = () => {
    if (this.#scrollScheduled) return;

    this.#scrollScheduled = true;

    requestAnimationFrame(() => {
      const stickyMode = this.getAttribute('sticky');
      if (!this.#offscreen && stickyMode !== 'always') {
        this.#scrollScheduled = false;
        return;
      }

      const scrollTop = document.scrollingElement?.scrollTop ?? 0;
      const isScrollingUp = scrollTop < this.#lastScrollTop;
      const top = this.#headerOffsetTop - scrollTop;

      if (this.#timeout) {
        clearTimeout(this.#timeout);
        this.#timeout = null;
      }

      if (stickyMode === 'always') {
        this.dataset.scrollDirection = top >= 0 ? 'none' : (isScrollingUp ? 'up' : 'down');
        this.#lastScrollTop = scrollTop;
        this.#scrollScheduled = false;
        return;
      }

      if (isScrollingUp) {
        this.removeAttribute('data-animating');

        if (top >= 0) {
          this.#offscreen = false;
          this.dataset.stickyState = 'inactive';
          this.dataset.scrollDirection = 'none';
        } else {
          this.dataset.stickyState = 'active';
          this.dataset.scrollDirection = 'up';
        }
      } else if (this.dataset.stickyState === 'active') {
        this.dataset.scrollDirection = 'none';
        this.setAttribute('data-animating', '');

        this.#timeout = setTimeout(() => {
          this.dataset.stickyState = 'idle';
          this.removeAttribute('data-animating');
        }, this.#animationDelay);
      } else {
        this.dataset.scrollDirection = 'none';
        this.dataset.stickyState = 'idle';
      }

      this.#lastScrollTop = scrollTop;
      this.#scrollScheduled = false;
    });
  };

  connectedCallback() {
    super.connectedCallback();

    this.#resizeObserver.observe(this);
    this.#headerOffsetTop = this.offsetTop;

    this.addEventListener('overflowMinimum', this.#handleOverflowMinimum);

    const stickyMode = this.getAttribute('sticky');
    if (stickyMode) {
      this.#observeStickyPosition(stickyMode === 'always');

      if (stickyMode === 'scroll-up' || stickyMode === 'always') {
        document.addEventListener('scroll', this.#handleWindowScroll, { passive: true });
      }
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#resizeObserver.disconnect();
    this.#intersectionObserver?.disconnect();
    this.removeEventListener('overflowMinimum', this.#handleOverflowMinimum);
    document.removeEventListener('scroll', this.#handleWindowScroll);
    document.body.style.setProperty('--header-height', '0px');
  }
}

if (!customElements.get('header-component')) {
  customElements.define('header-component', HeaderComponent);
}

/* -------------------------------------------------------------------------- */
/* Header group height throttling (Canyon core safe patch)                     */
/* -------------------------------------------------------------------------- */

let headerGroupHeightScheduled = false;

function scheduleHeaderGroupHeight(header, headerGroup) {
  if (headerGroupHeightScheduled) return;

  headerGroupHeightScheduled = true;

  requestAnimationFrame(() => {
    calculateHeaderGroupHeight(header, headerGroup);
    headerGroupHeightScheduled = false;
  });
}

onDocumentLoaded(() => {
  const header = document.querySelector('#header-component');
  const headerGroup = document.querySelector('#header-group');

  if (!headerGroup) return;

  const resizeObserver = new ResizeObserver(() => {
    scheduleHeaderGroupHeight(header, headerGroup);
  });

  Array.from(headerGroup.children).forEach((element) => {
    if (element !== header && element instanceof HTMLElement) {
      resizeObserver.observe(element);
    }
  });

  const mutationObserver = new MutationObserver(() => {
    Array.from(headerGroup.children).forEach((element) => {
      if (element !== header && element instanceof HTMLElement) {
        resizeObserver.observe(element);
      }
    });

    scheduleHeaderGroupHeight(header, headerGroup);
  });

  mutationObserver.observe(headerGroup, { childList: true });
});
